/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar, { MobileBar } from './TopBar';
import { useAuthStore } from '../../store/authStore';
import { useTour } from '../../hooks/useTour';
import { SidebarProvider } from '../../contexts/SidebarContext';
import QuickNotesFloat from '../QuickNotesFloat';
import KYCBanner from '../kyc/KYCBanner';
import NegativeBalanceRechargeModal from '../wallet/NegativeBalanceRechargeModal';
import ProjectOnboardingModal from '../projects/ProjectOnboardingModal';
import { useWallet } from '../../hooks/useWallet';
import { useProjects } from '../../hooks/useProjects';
import { isQuickNotesEnabled, isKYCEnforcementEnabled } from '../../config/featureFlags';

const DashboardContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, checkAuth, user } = useAuthStore();
  const { startTour } = useTour();
  const { wallet } = useWallet();
  const { projects, isLoading: projectsLoading, isFetched: projectsFetched } = useProjects();
  const [showOnboarding, setShowOnboarding] = React.useState(false);

  // Hide floating button on the Quick Notes page
  const hideFloatingNotes = location.pathname === '/quick-notes';

  useEffect(() => {
    checkAuth().catch((error) => {
      console.error('Auth check failed:', error);
    });
  }, [checkAuth]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (user?.show_tour) {
      startTour();
    }
  }, [user?.show_tour, startTour]);

  // Show onboarding modal when user has no projects
  useEffect(() => {
    if (projectsFetched && !projectsLoading && projects.length === 0) {
      setShowOnboarding(true);
    }
  }, [projectsFetched, projectsLoading, projects.length]);

  // Legacy "open the advisor" event (was the floating widget) now lands on /advisor.
  useEffect(() => {
    const onOpen = () => navigate('/advisor');
    window.addEventListener('podstack-advisor:open', onOpen);
    return () => window.removeEventListener('podstack-advisor:open', onOpen);
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ps-page">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-ps-raised border-t-ps-accent rounded-full animate-spin"></div>
          <p className="text-ps-muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-ps-page text-ps-text flex max-[820px]:flex-col">
      <MobileBar />
      <Sidebar />

      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar />
        {/* KYC Pending Banner */}
        {isKYCEnforcementEnabled() && <KYCBanner />}
        <main className="flex-1 min-w-0 relative">
          <Outlet />
        </main>
      </div>

      {/* Force-recharge modal - non-dismissible while wallet balance is negative.
          Backend (LowBalancePodStopper) auto-stops pods at the same threshold;
          this modal blocks the UI so the user is funnelled into recharging. */}
      <NegativeBalanceRechargeModal
        isOpen={(wallet?.balance ?? 0) < 0}
        balance={wallet?.balance ?? 0}
      />

      {/* Project Onboarding Modal */}
      <ProjectOnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />

      {/* Floating Quick Notes Button */}
      {isQuickNotesEnabled() && !hideFloatingNotes && <QuickNotesFloat />}
    </div>
  );
};

const DashboardLayout: React.FC = () => {
  return (
    <SidebarProvider>
      <DashboardContent />
    </SidebarProvider>
  );
};

export default DashboardLayout;
